/**
 * ModeSelector Component (Issue #302)
 *
 * Dropdown/popover for selecting chat modes.
 * Shows available modes with icons, colors, and descriptions.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Typography,
  alpha,
  CircularProgress,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ArchitectureIcon from '@mui/icons-material/Architecture';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckIcon from '@mui/icons-material/Check';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  selectActiveModes,
  selectCurrentMode,
  selectModeSwitching,
  selectShowModeSelector,
  setCurrentMode,
  setShowModeSelector,
  switchMode,
} from '@features/chat/chatModeSlice';
import { ChatMode } from '../../types/chat';

// Icon mapping for modes
const MODE_ICONS: Record<string, React.ReactNode> = {
  help: <HelpOutlineIcon />,
  settings: <SettingsIcon />,
  ask: <QuestionAnswerIcon />,
  plan: <ArchitectureIcon />,
  drbfm: <AssessmentIcon />,
};

interface ModeSelectorProps {
  anchorEl?: HTMLElement | null;
  threadId?: string;
  onClose?: () => void;
  onModeSelect?: (mode: ChatMode) => void;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({
  anchorEl,
  threadId,
  onClose,
  onModeSelect,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const modes = useAppSelector(selectActiveModes);
  const currentModeId = useAppSelector(selectCurrentMode);
  const showSelector = useAppSelector(selectShowModeSelector);
  const isSwitching = useAppSelector(selectModeSwitching);

  const isOpen = showSelector && Boolean(anchorEl);

  const handleClose = useCallback(() => {
    dispatch(setShowModeSelector(false));
    onClose?.();
  }, [dispatch, onClose]);

  const handleModeSelect = useCallback(
    (mode: ChatMode) => {
      if (mode.id === currentModeId) {
        handleClose();
        return;
      }

      // Use async switchMode for persistence if thread exists, otherwise use sync setCurrentMode
      if (threadId) {
        dispatch(
          switchMode({
            threadId,
            request: { mode_id: mode.id },
          })
        );
      } else {
        dispatch(setCurrentMode(mode.id));
      }
      onModeSelect?.(mode);
      handleClose();
    },
    [currentModeId, dispatch, handleClose, onModeSelect, threadId]
  );

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      slotProps={{
        paper: {
          sx: {
            minWidth: 280,
            maxWidth: 360,
            borderRadius: 2,
            boxShadow: (theme) => theme.shadows[8],
          },
        },
      }}
    >
      <Paper elevation={0}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" color="text.secondary">
            {t('chat.modeSelectorTitle')}
          </Typography>
        </Box>
        <List dense sx={{ py: 0.5 }}>
          {modes.map((mode) => {
            const isSelected = mode.id === currentModeId;
            const icon = MODE_ICONS[mode.id] || <QuestionAnswerIcon />;

            return (
              <ListItemButton
                key={mode.id}
                onClick={() => handleModeSelect(mode)}
                disabled={isSwitching}
                selected={isSelected}
                sx={{
                  borderRadius: 1,
                  mx: 0.5,
                  my: 0.25,
                  '&.Mui-selected': {
                    backgroundColor: alpha(mode.color, 0.1),
                    '&:hover': {
                      backgroundColor: alpha(mode.color, 0.15),
                    },
                  },
                  '&:hover': {
                    backgroundColor: alpha(mode.color, 0.08),
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: mode.color,
                  }}
                >
                  {icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isSelected ? 600 : 400,
                          color: isSelected ? mode.color : 'text.primary',
                        }}
                      >
                        {t(`chat.modes.${mode.id}.name`, mode.name)}
                      </Typography>
                      {isSelected && (
                        <CheckIcon
                          sx={{
                            fontSize: 16,
                            color: mode.color,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={t(`chat.modes.${mode.id}.description`, mode.description || '')}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    sx: { opacity: 0.7 },
                  }}
                />
                {isSwitching && isSelected && (
                  <CircularProgress
                    size={16}
                    sx={{ color: mode.color, ml: 1 }}
                  />
                )}
              </ListItemButton>
            );
          })}
        </List>
        <Box
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            backgroundColor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center' }}
          >
            {t('chat.modeSelectorHint')}
          </Typography>
        </Box>
      </Paper>
    </Popover>
  );
};

export default ModeSelector;
